import { useReducer, useCallback, useRef, useState } from 'react';
import type {
  Message,
  StreamEvent,
  ToolCall,
  StreamElement,
  NestedToolCall,
  SubAgentBlock,
  ThinkingPhase,
  ChatAction,
} from '../types/chat';
import { getAuthToken } from './useAuth';

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Generate or retrieve session ID (persists across page refreshes)
export function getSessionId(): string {
  const existing = sessionStorage.getItem('chat_session_id');
  if (existing) return existing;
  const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('chat_session_id', newId);
  return newId;
}

/**
 * Build the API endpoint URL and headers for chat streaming.
 *
 * Always uses the AgentCore protocol:
 *   - POST to AgentCore /invocations endpoint
 *   - Session ID via X-Amzn-Bedrock-AgentCore-Runtime-Session-Id header
 *   - Auth via Authorization Bearer header
 *   - Body: { prompt: message }
 */
function buildStreamRequest(message: string, sessionId: string, authToken: string | null, promptType?: string, metadata?: Record<string, unknown>) {
  const endpoint = import.meta.env.VITE_AGENTCORE_ENDPOINT || 'http://localhost:8080/invocations';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionId,
  };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  const payload: Record<string, unknown> = { prompt: message };
  if (promptType) {
    payload.prompt_type = promptType;
  }
  if (metadata) {
    Object.assign(payload, metadata);
  }
  return {
    url: endpoint,
    headers,
    body: JSON.stringify(payload),
  };
}

/**
 * Helper: Find or create a subagent block in the elements array.
 * Returns the updated elements array and the index of the subagent block.
 */
function findOrCreateSubAgentBlock(
  elements: StreamElement[],
  parentToolId: string,
  agentName: string,
  initialPhase: ThinkingPhase
): { elements: StreamElement[]; subAgentIdx: number } {
  const existingIdx = elements.findIndex(
    el => el.type === 'subagent' && el.subAgentBlock.parentToolId === parentToolId
  );

  if (existingIdx >= 0) {
    return { elements, subAgentIdx: existingIdx };
  }

  // Create new subagent block - find where to insert (after parent delegation tool)
  const parentIdx = elements.findIndex(
    el => el.type === 'tool' && el.toolCall.name.includes(`delegate_to_${agentName}`)
  );

  const newBlock: StreamElement = {
    type: 'subagent',
    subAgentBlock: {
      id: generateId(),
      parentToolId,
      agentName,
      phases: [initialPhase],
      status: 'running',
    },
  };

  const updated = [...elements];
  if (parentIdx >= 0) {
    // Insert after parent delegation tool
    updated.splice(parentIdx + 1, 0, newBlock);
    return { elements: updated, subAgentIdx: parentIdx + 1 };
  }

  // No parent found, append at end
  updated.push(newBlock);
  return { elements: updated, subAgentIdx: updated.length - 1 };
}

/**
 * Helper: Mark all tools and subagent blocks as complete
 */
function markAllComplete(msg: Message): Message {
  const updatedStreamElements = msg.streamElements?.map(el => {
    if (el.type === 'tool' && el.toolCall.status === 'running') {
      return { ...el, toolCall: { ...el.toolCall, status: 'complete' as const } };
    }
    if (el.type === 'subagent' && el.subAgentBlock.status === 'running') {
      return {
        ...el,
        subAgentBlock: {
          ...el.subAgentBlock,
          status: 'complete' as const,
          phases: el.subAgentBlock.phases.map(phase => ({
            ...phase,
            tools: phase.tools.map(t =>
              t.status === 'running' ? { ...t, status: 'complete' as const } : t
            ),
          })),
        },
      };
    }
    return el;
  });
  const updatedToolCalls = msg.toolCalls?.map(tc =>
    tc.status === 'running' ? { ...tc, status: 'complete' as const } : tc
  );
  return {
    ...msg,
    streamElements: updatedStreamElements,
    toolCalls: updatedToolCalls,
  };
}

/**
 * Chat reducer - handles all state updates sequentially to prevent race conditions.
 * This is critical for parallel sub-agent events that arrive in quick succession.
 */
function chatReducer(state: Message[], action: ChatAction): Message[] {
  switch (action.type) {
    case 'ADD_MESSAGES':
      return [...state, ...action.messages];

    case 'APPEND_THINKING':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        const elements = msg.streamElements || [];
        const lastEl = elements[elements.length - 1];

        // If last element is thinking, append to it
        if (lastEl?.type === 'thinking') {
          const updated = [...elements];
          updated[updated.length - 1] = {
            ...lastEl,
            content: lastEl.content + action.content,
          };
          return { ...msg, streamElements: updated };
        }

        // Otherwise start new thinking block
        const newBlock: StreamElement = {
          type: 'thinking',
          id: generateId(),
          content: action.content,
        };
        return { ...msg, streamElements: [...elements, newBlock] };
      });

    case 'ADD_TOOL': {
      const toolElement: StreamElement = { type: 'tool', toolCall: action.toolCall };
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        return {
          ...msg,
          streamElements: [...(msg.streamElements || []), toolElement],
          toolCalls: [...(msg.toolCalls || []), action.toolCall],
        };
      });
    }

    case 'UPDATE_TOOL_INPUT':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        return {
          ...msg,
          streamElements: msg.streamElements?.map(el => {
            if (el.type === 'tool' && el.toolCall.id === action.toolId) {
              return { ...el, toolCall: { ...el.toolCall, input: action.input } };
            }
            return el;
          }),
          toolCalls: msg.toolCalls?.map(tc => {
            if (tc.id === action.toolId) {
              return { ...tc, input: action.input };
            }
            return tc;
          }),
        };
      });

    case 'ADD_TOOL_RESULT':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        return {
          ...msg,
          streamElements: msg.streamElements?.map(el => {
            if (el.type === 'tool') {
              const tc = el.toolCall;
              if (tc.id === action.toolId || (!action.toolId && tc.status === 'running')) {
                return { ...el, toolCall: { ...tc, result: action.content, status: 'complete' as const } };
              }
            }
            return el;
          }),
          toolCalls: msg.toolCalls?.map(tc => {
            if (tc.id === action.toolId || (!action.toolId && tc.status === 'running')) {
              return { ...tc, result: action.content, status: 'complete' as const };
            }
            return tc;
          }),
        };
      });

    case 'ADD_NESTED_THINKING':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;

        const elements = msg.streamElements || [];
        const existingIdx = elements.findIndex(
          el => el.type === 'subagent' && el.subAgentBlock.parentToolId === action.parentToolId
        );

        if (existingIdx >= 0) {
          // Update existing subagent block
          const updated = [...elements];
          const existing = updated[existingIdx] as { type: 'subagent'; subAgentBlock: SubAgentBlock };
          const phases = existing.subAgentBlock.phases;
          const lastPhase = phases[phases.length - 1];

          let newPhases: ThinkingPhase[];
          if (!lastPhase || lastPhase.tools.length > 0) {
            // Create new phase if: no phases exist OR last phase has tools
            newPhases = [...phases, { id: generateId(), thinking: action.content, tools: [] }];
          } else {
            // Append to existing phase's thinking
            newPhases = [...phases];
            newPhases[newPhases.length - 1] = {
              ...lastPhase,
              thinking: lastPhase.thinking + action.content,
            };
          }

          updated[existingIdx] = {
            ...existing,
            subAgentBlock: {
              ...existing.subAgentBlock,
              phases: newPhases,
            },
          };
          return { ...msg, streamElements: updated };
        }

        // Create new subagent block using helper
        const initialPhase: ThinkingPhase = {
          id: generateId(),
          thinking: action.content,
          tools: [],
        };
        const helperResult = findOrCreateSubAgentBlock(elements, action.parentToolId, action.agentName, initialPhase);
        return { ...msg, streamElements: helperResult.elements };
      });

    case 'ADD_NESTED_TOOL':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;

        const elements = msg.streamElements || [];
        const existingIdx = elements.findIndex(
          el => el.type === 'subagent' && el.subAgentBlock.parentToolId === action.parentToolId
        );

        if (existingIdx >= 0) {
          // Add tool to existing subagent block's last phase
          const updated = [...elements];
          const existing = updated[existingIdx] as { type: 'subagent'; subAgentBlock: SubAgentBlock };
          const phases = existing.subAgentBlock.phases;

          let newPhases: ThinkingPhase[];
          if (phases.length === 0) {
            // No phases exist - create one with empty thinking
            newPhases = [{ id: generateId(), thinking: '', tools: [action.nestedTool] }];
          } else {
            // Add new tool to last phase
            newPhases = [...phases];
            const lastPhase = newPhases[newPhases.length - 1];
            newPhases[newPhases.length - 1] = {
              ...lastPhase,
              tools: [...lastPhase.tools, action.nestedTool],
            };
          }

          updated[existingIdx] = {
            ...existing,
            subAgentBlock: {
              ...existing.subAgentBlock,
              phases: newPhases,
            },
          };
          return { ...msg, streamElements: updated };
        }

        // No subagent block exists yet - create one with initial phase containing the tool
        const initialPhase: ThinkingPhase = {
          id: generateId(),
          thinking: '',
          tools: [action.nestedTool],
        };
        const result = findOrCreateSubAgentBlock(elements, action.parentToolId, action.agentName, initialPhase);
        return { ...msg, streamElements: result.elements };
      });

    case 'UPDATE_NESTED_TOOL_INPUT':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        return {
          ...msg,
          streamElements: msg.streamElements?.map(el => {
            if (el.type !== 'subagent' || el.subAgentBlock.parentToolId !== action.parentToolId) {
              return el;
            }
            return {
              ...el,
              subAgentBlock: {
                ...el.subAgentBlock,
                phases: el.subAgentBlock.phases.map(phase => ({
                  ...phase,
                  tools: phase.tools.map(t =>
                    t.id === action.toolId ? { ...t, input: action.input } : t
                  ),
                })),
              },
            };
          }),
        };
      });

    case 'ADD_NESTED_TOOL_RESULT':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        return {
          ...msg,
          streamElements: msg.streamElements?.map(el => {
            if (el.type !== 'subagent' || el.subAgentBlock.parentToolId !== action.parentToolId) {
              return el;
            }
            return {
              ...el,
              subAgentBlock: {
                ...el.subAgentBlock,
                phases: el.subAgentBlock.phases.map(phase => ({
                  ...phase,
                  tools: phase.tools.map(t =>
                    t.id === action.toolId ? { ...t, result: action.content || '', status: 'complete' as const } : t
                  ),
                })),
              },
            };
          }),
        };
      });

    case 'NESTED_DONE':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        return {
          ...msg,
          streamElements: msg.streamElements?.map(el => {
            if (el.type !== 'subagent' || el.subAgentBlock.parentToolId !== action.parentToolId) {
              return el;
            }
            // Mark subagent block and all tools in all phases as complete
            return {
              ...el,
              subAgentBlock: {
                ...el.subAgentBlock,
                status: 'complete' as const,
                phases: el.subAgentBlock.phases.map(phase => ({
                  ...phase,
                  tools: phase.tools.map(t =>
                    t.status === 'running' ? { ...t, status: 'complete' as const } : t
                  ),
                })),
              },
            };
          }),
        };
      });

    case 'APPEND_TEXT':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        const completed = markAllComplete(msg);
        return {
          ...completed,
          content: msg.content + action.content,
        };
      });

    case 'STREAM_DONE':
      return state.map(msg => {
        if (msg.id !== action.messageId) return msg;
        const completed = markAllComplete(msg);
        return {
          ...completed,
          isStreaming: false,
        };
      });

    case 'REMOVE_MESSAGE':
      return state.filter(msg => msg.id !== action.messageId);

    case 'STOP_STREAMING':
      return state.map(msg =>
        msg.isStreaming ? { ...msg, isStreaming: false } : msg
      );

    case 'CLEAR_MESSAGES':
      return [];

    default:
      return state;
  }
}

export function useChat() {
  const [messages, dispatch] = useReducer(chatReducer, []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Session ID is stable for this browser tab (regenerated on clearMessages)
  const [sessionId, setSessionId] = useState(getSessionId);

  const sendMessage = useCallback(async (content: string, promptType?: string, metadata?: Record<string, unknown>) => {
    if (!content.trim() || isLoading) return;

    setError(null);
    setIsLoading(true);

    // Abort any existing request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    // Add user message
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    // Add placeholder assistant message
    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      streamElements: [],
      toolCalls: [],
    };

    dispatch({ type: 'ADD_MESSAGES', messages: [userMessage, assistantMessage] });

    try {
      const authToken = getAuthToken();
      const { url, headers, body } = buildStreamRequest(content, sessionId, authToken, promptType, metadata);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data: StreamEvent = JSON.parse(line.slice(6));

              if (data.type === 'error') {
                setError(data.content || 'An error occurred');
                break;
              }

              // Heartbeat events keep the connection alive during long waits
              if (data.type === 'heartbeat') {
                continue;
              }

              if (data.type === 'done') {
                dispatch({ type: 'STREAM_DONE', messageId: assistantMessageId });
                break;
              }

              if (data.type === 'text' && data.content) {
                dispatch({ type: 'APPEND_TEXT', messageId: assistantMessageId, content: data.content });
              }

              if (data.type === 'thinking' && data.content) {
                dispatch({ type: 'APPEND_THINKING', messageId: assistantMessageId, content: data.content });
              }

              if (data.type === 'tool_use' && data.name) {
                const toolCall: ToolCall = {
                  name: data.name,
                  id: data.id || generateId(),
                  input: data.input || {},
                  status: 'running',
                };
                dispatch({ type: 'ADD_TOOL', messageId: assistantMessageId, toolCall });
              }

              if (data.type === 'tool_input_update' && data.id && data.input) {
                dispatch({
                  type: 'UPDATE_TOOL_INPUT',
                  messageId: assistantMessageId,
                  toolId: data.id,
                  input: data.input,
                });
              }

              if (data.type === 'tool_result') {
                dispatch({
                  type: 'ADD_TOOL_RESULT',
                  messageId: assistantMessageId,
                  toolId: data.id || '',
                  content: data.content,
                });
              }

              if (data.type === 'nested_thinking' && data.parent_tool_id && data.content) {
                dispatch({
                  type: 'ADD_NESTED_THINKING',
                  messageId: assistantMessageId,
                  parentToolId: data.parent_tool_id,
                  agentName: data.agent_name || 'specialist',
                  content: data.content,
                });
              }

              if (data.type === 'nested_tool_use' && data.parent_tool_id && data.name) {
                const nestedTool: NestedToolCall = {
                  name: data.name,
                  id: data.id || generateId(),
                  input: data.input || {},
                  status: 'running',
                };
                dispatch({
                  type: 'ADD_NESTED_TOOL',
                  messageId: assistantMessageId,
                  parentToolId: data.parent_tool_id,
                  agentName: data.agent_name || 'specialist',
                  nestedTool,
                });
              }

              if (data.type === 'nested_tool_input_update' && data.parent_tool_id && data.id && data.input) {
                dispatch({
                  type: 'UPDATE_NESTED_TOOL_INPUT',
                  messageId: assistantMessageId,
                  parentToolId: data.parent_tool_id,
                  toolId: data.id,
                  input: data.input,
                });
              }

              if (data.type === 'nested_tool_result' && data.parent_tool_id && data.id) {
                dispatch({
                  type: 'ADD_NESTED_TOOL_RESULT',
                  messageId: assistantMessageId,
                  parentToolId: data.parent_tool_id,
                  toolId: data.id,
                  content: data.content,
                });
              }

              if (data.type === 'nested_done' && data.parent_tool_id) {
                dispatch({
                  type: 'NESTED_DONE',
                  messageId: assistantMessageId,
                  parentToolId: data.parent_tool_id,
                });
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        const errorMessage = (err as Error).message || 'An error occurred';
        setError(errorMessage);
        // Remove the empty assistant message on error
        dispatch({ type: 'REMOVE_MESSAGE', messageId: assistantMessageId });
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, sessionId]);

  const stopStream = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
    dispatch({ type: 'STOP_STREAMING' });
  }, []);

  const clearMessages = useCallback(async () => {
    // Abort any in-flight stream
    abortControllerRef.current?.abort();
    setIsLoading(false);

    // Clear messages and errors
    dispatch({ type: 'CLEAR_MESSAGES' });
    setError(null);

    // Generate a fresh session ID
    sessionStorage.removeItem('chat_session_id');
    const newId = getSessionId();
    setSessionId(newId);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    stopStream,
    clearMessages,
    sessionId,
  };
}
